import { captureError } from '@smg-real-estate/browser-rum';
import { RankType, type Listing } from '@smg-real-estate/hg-rets-schema';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { TopListingAbos, ToplistingPrices } from '@/api/toplistingService';
import * as ToplistingApiService from '@/api/toplistingService';
import { ContractModel } from '@/api/toplistingService';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useInsertionsStore } from '@/stores/insertions/useInsertionsStore';
import {
  createRegionalHighlightActions,
  getInitialRegionalHighlightApiStatus,
  type RegionalHighlightApiStatus,
  type RegionalHighlightErrors,
} from '@/stores/toplisting/regionalHighlight';
import { resolveInsertionOwnerId } from '@/stores/toplisting/resolveInsertionOwnerId';
import { ApiCallStatus, findInsertionById } from '@/stores/types/insertions';
import { useUserStore } from '@/stores/user/useUserStore';
import {
  ToplistingModalAction,
  ToplistingVariant,
  type BookToplistingRequest,
  type Insertion,
  type ToplistingData,
} from '@/types';
import type {
  RegionalHighlightAssignmentResponse,
  RegionalHighlightCallbackResponse,
  RegionalHighlightListingStatusesByListingId,
  RegionalHighlightPurchaseOptionsResponse,
  RegionalHighlightPurchaseOrder,
  RegionalHighlightPurchaseOrdersResponse,
  RegionalHighlightSlot,
  RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type {
  RegionalHighlightApiStatus,
  RegionalHighlightErrors,
  RegionalHighlightOperation,
} from '@/stores/toplisting/regionalHighlight';

const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 10000];
const NO_TOPLISTING_SIGNATURE = 'none';

const getToplistingSignature = (data: ToplistingData): string =>
  [data.listingType, data.contractModel, data.beginRequest, data.endRequest ?? ''].join('|');

const getExpectedSignatureFromBooking = (booking: BookToplistingRequest): string =>
  [booking.rankType, booking.contractModel, booking.beginRequest, booking.endRequest ?? ''].join(
    '|',
  );

const getExpectedSignatureFromFunnelListing = (listing?: Listing): string | null => {
  const valueAddedServices = listing?.valueAddedServices;
  if (!valueAddedServices) {
    return null;
  }

  if (valueAddedServices.isLifetimeToplisting) {
    return [
      RankType.Top,
      ContractModel.LIFETIME,
      valueAddedServices.rank?.start ?? '',
      valueAddedServices.rank?.end ?? '',
    ].join('|');
  }

  const rankType = valueAddedServices.rank?.type;
  if (!rankType) {
    return null;
  }

  const listingRankType = rankType === RankType.Premium ? RankType.Premium : RankType.Top;

  return [
    listingRankType,
    ContractModel.DAILY,
    valueAddedServices.rank?.start ?? '',
    valueAddedServices.rank?.end ?? '',
  ].join('|');
};

const hasActiveToplisting = (data?: ToplistingData): boolean =>
  data?.listingType === RankType.Top || data?.listingType === RankType.Premium;

const matchesExpectedSignature = (
  data: ToplistingData | undefined,
  expectedSignature: string,
): boolean => {
  if (expectedSignature === NO_TOPLISTING_SIGNATURE) {
    return !hasActiveToplisting(data);
  }

  if (!data) {
    return false;
  }

  const actualSignature = getToplistingSignature(data);
  return (
    actualSignature === expectedSignature || actualSignature.startsWith(`${expectedSignature}|`)
  );
};

export const useToplistingStore = defineStore(
  'toplisting',
  // eslint-disable-next-line max-statements -- setup store returns state, computed, and actions together
  () => {
    const toplistingInsertionId = ref<string | undefined>(undefined);
    const toplistingApiCallStatus = ref<ApiCallStatus>(ApiCallStatus.READY);
    const insertionIdsWithRecentlyUpdatedToplisting = ref<string[]>([]);
    const insertionIdsWithRecentlyDeletedToplisting = ref<string[]>([]);
    const expectedToplistingSignatures = ref<Record<string, string>>({});
    const toplistingInsertion = ref<Insertion | undefined>(undefined);
    const topListingAbos = ref<TopListingAbos | undefined>(undefined);
    const topListingPrices = ref<ToplistingPrices | undefined>(undefined);
    const regionalHighlightSlots = ref<RegionalHighlightSlot[] | undefined>(undefined);
    const regionalHighlightSlotsResponse = ref<RegionalHighlightSlotsResponse | undefined>(
      undefined,
    );
    const regionalHighlightListingStatuses = ref<
      RegionalHighlightListingStatusesByListingId | undefined
    >(undefined);
    const regionalHighlightPurchaseOptions = ref<
      RegionalHighlightPurchaseOptionsResponse | undefined
    >(undefined);
    const regionalHighlightPurchaseOrders = ref<
      RegionalHighlightPurchaseOrdersResponse | undefined
    >(undefined);
    const regionalHighlightLastPurchaseOrder = ref<RegionalHighlightPurchaseOrder | undefined>(
      undefined,
    );
    const regionalHighlightApiStatus = ref<RegionalHighlightApiStatus>(
      getInitialRegionalHighlightApiStatus(),
    );
    const regionalHighlightErrors = ref<RegionalHighlightErrors>({});
    const regionalHighlightLastAssignment = ref<RegionalHighlightAssignmentResponse | undefined>(
      undefined,
    );
    const regionalHighlightLastCallback = ref<RegionalHighlightCallbackResponse | undefined>(
      undefined,
    );
    const insertionIdsWithPendingRegionalHighlight = ref<string[]>([]);

    const regionalHighlight = createRegionalHighlightActions({
      regionalHighlightSlots,
      regionalHighlightSlotsResponse,
      regionalHighlightListingStatuses,
      regionalHighlightPurchaseOptions,
      regionalHighlightPurchaseOrders,
      regionalHighlightLastPurchaseOrder,
      regionalHighlightApiStatus,
      regionalHighlightErrors,
      regionalHighlightLastAssignment,
      regionalHighlightLastCallback,
      insertionIdsWithPendingRegionalHighlight,
    });

    const isToplistingApiCallInProgress = computed(
      () => toplistingApiCallStatus.value === ApiCallStatus.IN_PROGRESS,
    );

    const getToplistingDataByInsertionId = (id: string): ToplistingData | undefined =>
      toplistingInsertion.value !== undefined
        ? toplistingInsertion.value.toplistingData
        : findInsertionById(useInsertionsStore().insertions, id)?.toplistingData;

    const clearExpectedToplistingSignature = (insertionId: string): void => {
      const { [insertionId]: _removed, ...remainingSignatures } =
        expectedToplistingSignatures.value;
      expectedToplistingSignatures.value = remainingSignatures;
    };

    const appendToInsertionIdsWithRecentlyUpdatedToplisting = (payload: {
      insertionId: string;
      expectedSignature: string;
    }): void => {
      insertionIdsWithRecentlyUpdatedToplisting.value = [
        ...insertionIdsWithRecentlyUpdatedToplisting.value,
        payload.insertionId,
      ];
      expectedToplistingSignatures.value = {
        ...expectedToplistingSignatures.value,
        [payload.insertionId]: payload.expectedSignature,
      };
    };

    const removeFromInsertionIdsWithRecentlyUpdatedToplisting = (
      insertionIdWithRecentlyUpdatedToplisting: string,
    ): void => {
      insertionIdsWithRecentlyUpdatedToplisting.value =
        insertionIdsWithRecentlyUpdatedToplisting.value.filter(
          (insertionId) => insertionId !== insertionIdWithRecentlyUpdatedToplisting,
        );
      clearExpectedToplistingSignature(insertionIdWithRecentlyUpdatedToplisting);
    };

    const appendToInsertionIdsWithRecentlyDeletedToplisting = (
      insertionIdWithRecentlyDeletedToplisting: string,
    ): void => {
      insertionIdsWithRecentlyDeletedToplisting.value = [
        ...insertionIdsWithRecentlyDeletedToplisting.value,
        insertionIdWithRecentlyDeletedToplisting,
      ];
      expectedToplistingSignatures.value = {
        ...expectedToplistingSignatures.value,
        [insertionIdWithRecentlyDeletedToplisting]: NO_TOPLISTING_SIGNATURE,
      };
    };

    const removeFromInsertionIdsWithRecentlyDeletedToplisting = (
      insertionIdWithRecentlyDeletedToplisting: string,
    ): void => {
      insertionIdsWithRecentlyDeletedToplisting.value =
        insertionIdsWithRecentlyDeletedToplisting.value.filter(
          (insertionId) => insertionId !== insertionIdWithRecentlyDeletedToplisting,
        );
      clearExpectedToplistingSignature(insertionIdWithRecentlyDeletedToplisting);
    };

    const resolveUserIdForInsertion = (insertionId: string): string | undefined =>
      resolveInsertionOwnerId(insertionId, toplistingInsertion.value?.userId);

    const refetchToplistingIfMatches = async (
      userId: string,
      insertionId: string,
      expectedSignature: string,
    ): Promise<boolean> => {
      try {
        const updatedInsertion = await useInsertionsStore().refetchInsertionById({
          userId,
          insertionId,
        });

        return matchesExpectedSignature(updatedInsertion.toplistingData, expectedSignature);
      } catch (error) {
        captureError(error);
        return false;
      }
    };

    const finishToplistingProcessing = (insertionId: string, expectedSignature: string): void => {
      if (expectedSignature === NO_TOPLISTING_SIGNATURE) {
        removeFromInsertionIdsWithRecentlyDeletedToplisting(insertionId);
        return;
      }

      removeFromInsertionIdsWithRecentlyUpdatedToplisting(insertionId);
    };

    const pollToplistingUpdateStatus = async (insertionId: string): Promise<void> => {
      const expectedSignature = expectedToplistingSignatures.value[insertionId];
      if (!expectedSignature) {
        return;
      }

      const userId = resolveUserIdForInsertion(insertionId);
      if (!userId) {
        finishToplistingProcessing(insertionId, expectedSignature);
        return;
      }

      for (const delayMs of POLL_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));

        if (await refetchToplistingIfMatches(userId, insertionId, expectedSignature)) {
          finishToplistingProcessing(insertionId, expectedSignature);
          return;
        }
      }

      finishToplistingProcessing(insertionId, expectedSignature);
    };

    const startToplistingPolling = (insertionId: string): void => {
      pollToplistingUpdateStatus(insertionId).catch((error) => {
        captureError(error);
      });
    };

    const handleToplistingAsyncAction = async (
      action: () => Promise<unknown>,
    ): Promise<boolean> => {
      const insertionsStore = useInsertionsStore();
      try {
        toplistingApiCallStatus.value = ApiCallStatus.IN_PROGRESS;
        insertionsStore.apiCallStatus = ApiCallStatus.IN_PROGRESS;
        await action();
        toplistingApiCallStatus.value = ApiCallStatus.SUCCESS;
        insertionsStore.apiCallStatus = ApiCallStatus.SUCCESS;
        return true;
      } catch (error) {
        toplistingApiCallStatus.value = ApiCallStatus.READY;
        insertionsStore.apiCallStatus = ApiCallStatus.ERROR;
        captureError(error);
        return false;
      }
    };

    const fetchTopListingAbos = async (): Promise<void> => {
      // TODO: how to get the agencyId, before agencySwitcher is used?
      const activeAgencyId = useAgencyStore().activeAgencyId;

      if (!activeAgencyId) return;

      try {
        topListingAbos.value = await ToplistingApiService.fetchTopListingAbos(activeAgencyId);
      } catch (error) {
        captureError(error);
      }
    };

    const bookToplisting = async (data: {
      objectReferenceId: string;
      insertionId: string;
      toplisting: BookToplistingRequest;
      action: ToplistingModalAction;
    }): Promise<void> => {
      await handleToplistingAsyncAction(async () => {
        await ToplistingApiService.bookToplisting(data.objectReferenceId, data.toplisting);

        appendToInsertionIdsWithRecentlyUpdatedToplisting({
          insertionId: data.insertionId,
          expectedSignature: getExpectedSignatureFromBooking(data.toplisting),
        });

        useInsertionsStore().setListingActionSuccess(
          `manageAdsPage.toasts.toplistingActionSuccess.${data.toplisting.rankType}.${data.action}`,
        );

        startToplistingPolling(data.insertionId);
      });
    };

    const bookToplistingAbo = async (data: {
      objectReferenceId: string;
      insertionId: string;
      action?: ToplistingModalAction;
    }): Promise<void> => {
      // TODO: how to get the agencyId, before agencySwitcher is used?
      const activeAgencyId = useAgencyStore().activeAgencyId || useUserStore().logonId;

      await handleToplistingAsyncAction(async () => {
        await ToplistingApiService.bookToplistingAbo(data.objectReferenceId, activeAgencyId);

        appendToInsertionIdsWithRecentlyUpdatedToplisting({
          insertionId: data.insertionId,
          expectedSignature: `${RankType.Top}|${ContractModel.ABO}`,
        });

        useInsertionsStore().setListingActionSuccess(
          `manageAdsPage.toasts.toplistingActionSuccess.${ToplistingVariant.TopAbo}.CREATE`,
        );

        // Increase the TL Abo ActiveSlots by 1 to display the correct count until the next fetchTopListingAbos call
        const currentAbos = topListingAbos.value!;
        topListingAbos.value = {
          ...currentAbos,
          activeSlots: (currentAbos.activeSlots += 1),
        };

        startToplistingPolling(data.insertionId);
      });
    };

    const deleteToplisting = async (data: {
      insertionId: string;
      objectReferenceId: string;
    }): Promise<boolean> => {
      return handleToplistingAsyncAction(async () => {
        await ToplistingApiService.deleteToplisting(data.objectReferenceId);

        appendToInsertionIdsWithRecentlyDeletedToplisting(data.insertionId);

        useInsertionsStore().setListingActionSuccess(
          'manageAdsPage.toasts.toplistingWillBeDeactivated',
        );

        startToplistingPolling(data.insertionId);
      });
    };

    const trackToplistingFromFunnelListing = (payload: {
      insertionId: string;
      listing?: Listing;
    }): void => {
      const expectedSignature = getExpectedSignatureFromFunnelListing(payload.listing);
      if (!expectedSignature) {
        return;
      }

      const insertion = findInsertionById(useInsertionsStore().insertions, payload.insertionId);
      if (matchesExpectedSignature(insertion?.toplistingData, expectedSignature)) {
        return;
      }

      if (!insertionIdsWithRecentlyUpdatedToplisting.value.includes(payload.insertionId)) {
        appendToInsertionIdsWithRecentlyUpdatedToplisting({
          insertionId: payload.insertionId,
          expectedSignature,
        });
      }

      startToplistingPolling(payload.insertionId);
    };

    return {
      toplistingInsertionId,
      toplistingApiCallStatus,
      insertionIdsWithRecentlyUpdatedToplisting,
      insertionIdsWithRecentlyDeletedToplisting,
      expectedToplistingSignatures,
      toplistingInsertion,
      topListingAbos,
      topListingPrices,
      regionalHighlightSlots,
      regionalHighlightSlotsResponse,
      regionalHighlightListingStatuses,
      regionalHighlightPurchaseOptions,
      regionalHighlightPurchaseOrders,
      regionalHighlightLastPurchaseOrder,
      regionalHighlightApiStatus,
      regionalHighlightErrors,
      regionalHighlightLastAssignment,
      regionalHighlightLastCallback,
      insertionIdsWithPendingRegionalHighlight,
      isToplistingApiCallInProgress,
      getToplistingDataByInsertionId,
      fetchTopListingAbos,
      pollToplistingUpdateStatus,
      bookToplisting,
      bookToplistingAbo,
      deleteToplisting,
      trackToplistingFromFunnelListing,
      ...regionalHighlight,
    };
  },
);
